import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type { RapprochementLigneRow, RapprochementRow } from '@/lib/database.types';

export function useRapprochements(entrepriseId: string | null) {
  return useQuery({
    queryKey: ['rapprochements', entrepriseId],
    enabled: !!entrepriseId,
    queryFn: async (): Promise<RapprochementRow[]> => {
      if (!entrepriseId) return [];
      const { data, error } = await supabase
        .from('rapprochements')
        .select('*')
        .eq('entreprise_id', entrepriseId)
        .order('mois', { ascending: false });
      if (error) throw error;
      return data;
    },
  });
}

export function useRapprochementLignes(rapprochementId: string | null) {
  return useQuery({
    queryKey: ['rapprochement_lignes', rapprochementId],
    enabled: !!rapprochementId,
    queryFn: async (): Promise<RapprochementLigneRow[]> => {
      if (!rapprochementId) return [];
      const { data, error } = await supabase
        .from('rapprochement_lignes')
        .select('*')
        .eq('rapprochement_id', rapprochementId)
        .order('releve_date');
      if (error) throw error;
      return data;
    },
  });
}

export function useCreateRapprochement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      entreprise_id: string;
      compte_bancaire_id: string;
      mois: string;
    }): Promise<RapprochementRow> => {
      const { data, error } = await supabase
        .from('rapprochements')
        .insert(input)
        .select('*')
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['rapprochements'] }),
  });
}

export function useInsertRapprochementLignes() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      lignes: Array<{
        rapprochement_id: string;
        releve_date?: string | null;
        releve_libelle?: string | null;
        releve_montant?: number | null;
        releve_sens?: 'entree' | 'sortie' | null;
      }>,
    ): Promise<void> => {
      const { error } = await supabase.from('rapprochement_lignes').insert(lignes);
      if (error) throw error;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['rapprochement_lignes'] }),
  });
}

export function useUpdateRapprochementLigne() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      id: string;
      operation_id?: string | null;
      statut?: 'rapprochee' | 'partielle' | 'non_rapprochee_releve' | 'non_rapprochee_erp';
      commentaire?: string | null;
      rapprochee_manuellement?: boolean;
    }): Promise<void> => {
      const { id, ...rest } = input;
      const patch = {
        ...rest,
        ...(input.rapprochee_manuellement ? { rapprochee_at: new Date().toISOString() } : {}),
      };
      const { error } = await supabase.from('rapprochement_lignes').update(patch).eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['rapprochement_lignes'] }),
  });
}

export function useUpdateRapprochementStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; statut: 'en_cours' | 'valide' | 'archive' }) => {
      const patch = {
        statut: input.statut,
        ...(input.statut === 'valide' ? { validated_at: new Date().toISOString() } : {}),
      };
      const { error } = await supabase.from('rapprochements').update(patch).eq('id', input.id);
      if (error) throw error;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['rapprochements'] }),
  });
}
